import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../ui-components/ui/dialog';
import { Button } from '../../../ui-components/ui/button';
import { Input } from '../../../ui-components/ui/input';
import { Label } from '../../../ui-components/ui/label';
import { Textarea } from '../../../ui-components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui-components/ui/select';
import { Switch } from '../../../ui-components/ui/switch';
import { Badge } from '../../../ui-components/ui/badge';
import {
  Appointment,
  AppointmentType,
  AppointmentStatus,
  CreateAppointmentDto,
  UpdateAppointmentDto,
} from '../../../lib/interfaces/appointment.interface';
import { SelectableEntity } from '../../../lib/interfaces/selectable-entity.interface';
import { UserSelect } from '../../../ui-components/ui/user-select';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Link, Bell } from 'lucide-react';
import { useUser } from '../../../lib/hooks/use-user';
import { UserRole } from '../../../lib/interfaces/user';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment?: Appointment | null;
  defaultDealId?: string;
  defaultDealName?: string;
  defaultContactId?: string;
  defaultContactName?: string;
  defaultStart?: Date;
  defaultEnd?: Date;
  onSave: (data: CreateAppointmentDto | UpdateAppointmentDto, id?: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onMarkComplete?: (id: string, notes?: string) => Promise<void>;
}

const appointmentTypeLabels: Record<AppointmentType, string> = {
  [AppointmentType.CALL]: 'Anruf',
  [AppointmentType.SITE_VISIT]: 'Vor-Ort Termin',
  [AppointmentType.VIDEO]: 'Video-Konferenz',
  [AppointmentType.MEETING]: 'Meeting',
  [AppointmentType.OTHER]: 'Sonstiges',
  [AppointmentType.EMAIL]: 'E-Mail',
  [AppointmentType.TASK]: 'Aufgabe',
};

const reminderOptions = [
  { value: 0, label: 'Zum Zeitpunkt' },
  { value: 5, label: '5 Minuten vorher' },
  { value: 15, label: '15 Minuten vorher' },
  { value: 30, label: '30 Minuten vorher' },
  { value: 60, label: '1 Stunde vorher' },
  { value: 1440, label: '1 Tag vorher' },
];

export const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  appointment,
  defaultDealId,
  defaultDealName,
  defaultContactId,
  defaultContactName,
  defaultStart,
  defaultEnd,
  onSave,
  onDelete,
  onMarkComplete,
}) => {
  const { user } = useUser();
  const isAdmin = user?.role === UserRole.Admin || user?.role === UserRole.SuperAdmin;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<SelectableEntity[]>([]);
  const [showCompleteNotes, setShowCompleteNotes] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateAppointmentDto & { date: string; startTime: string; endTime: string }>();

  const allDay = watch('allDay');
  const type = watch('type');
  const reminderMinutes = watch('reminders.0.minutesBefore');

  useEffect(() => {
    if (appointment) {
      const start = moment(appointment.start);
      const end = moment(appointment.end);
      
      reset({
        title: appointment.title,
        description: appointment.description || '',
        location: appointment.location || '',
        type: appointment.type,
        allDay: appointment.allDay,
        date: start.format('YYYY-MM-DD'),
        startTime: start.format('HH:mm'),
        endTime: end.format('HH:mm'),
        videoConferenceLink: appointment.videoConferenceLink || '',
        dealId: appointment.dealId,
        contactId: appointment.contactId,
        participantUserIds: appointment.participantUserIds,
        timezone: appointment.timezone,
        reminders: appointment.reminders,
      });

      if (appointment.participants) {
        setSelectedParticipants(appointment.participants.map(p => ({
          _id: p._id,
          value: p.name,
        })));
      }
    } else {
      const start = defaultStart || new Date();
      const end = defaultEnd || moment(start).add(1, 'hour').toDate();
      
      reset({
        title: '',
        description: '',
        location: '',
        type: AppointmentType.MEETING,
        allDay: false,
        date: moment(start).format('YYYY-MM-DD'),
        startTime: moment(start).format('HH:mm'),
        endTime: moment(end).format('HH:mm'),
        videoConferenceLink: '',
        dealId: defaultDealId || '',
        contactId: defaultContactId,
        participantUserIds: [],
        timezone: 'Europe/Berlin',
        reminders: [{ minutesBefore: 15, channel: 'in_app' }],
      });
      setSelectedParticipants([]);
    }
  }, [appointment, defaultStart, defaultEnd, defaultDealId, defaultContactId, reset]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const start = allDay
        ? moment(data.date).startOf('day')
        : moment(`${data.date} ${data.startTime}`);
      const end = allDay
        ? moment(data.date).endOf('day')
        : moment(`${data.date} ${data.endTime}`);

      if (!allDay && end.isSameOrBefore(start)) {
        toast.error('Die Endzeit muss nach der Startzeit liegen');
        return;
      }

      const appointmentData: CreateAppointmentDto = {
        ...data,
        start: start.toISOString(),
        end: end.toISOString(),
        participantUserIds: selectedParticipants.map(p => p._id),
        reminders: data.reminders || [],
        allDay: allDay,
        timezone: 'Europe/Berlin',
      };

      await onSave(appointmentData, appointment?._id);
      onClose();
    } catch (error) {
      console.error('Error saving appointment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (appointment && onDelete && window.confirm('Möchten Sie diesen Termin wirklich absagen?')) {
      setIsSubmitting(true);
      try {
        await onDelete(appointment._id);
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleMarkComplete = async () => {
    if (appointment && onMarkComplete) {
      setIsSubmitting(true);
      try {
        await onMarkComplete(appointment._id, completeNotes);
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {appointment ? 'Termin bearbeiten' : 'Neuer Termin'}
          </DialogTitle>
        </DialogHeader>

        {appointment && appointment.status !== AppointmentStatus.SCHEDULED && (
          <div className="mb-4">
            <Badge variant={appointment.status === AppointmentStatus.DONE ? 'default' : 'secondary'}>
              {appointment.status === AppointmentStatus.DONE ? 'Erledigt' : 'Abgesagt'}
            </Badge>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              {...register('title', { required: 'Titel ist erforderlich' })}
              placeholder="z.B. Sicherheitsberatung"
              disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Typ *</Label>
              <Select
                value={type}
                onValueChange={(value) => setValue('type', value as AppointmentType)}
                disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(appointmentTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Datum *</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="date"
                  type="date"
                  {...register('date', { required: 'Datum ist erforderlich' })}
                  className="pl-10"
                  disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="allDay"
              checked={allDay}
              onCheckedChange={(checked) => setValue('allDay', checked)}
              disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
            />
            <Label htmlFor="allDay">Ganztägig</Label>
          </div>

          {!allDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Startzeit *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="startTime"
                    type="time"
                    {...register('startTime', { required: !allDay ? 'Startzeit ist erforderlich' : false })}
                    className="pl-10"
                    disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="endTime">Endzeit *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="endTime"
                    type="time"
                    {...register('endTime', { required: !allDay ? 'Endzeit ist erforderlich' : false })}
                    className="pl-10"
                    disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="location">Ort</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="location"
                {...register('location')}
                placeholder="z.B. Musterstraße 123, 12345 Berlin"
                className="pl-10"
                disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
              />
            </div>
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <Label htmlFor="participants">Teilnehmer</Label>
              <UserSelect
                value={selectedParticipants}
                onChange={setSelectedParticipants}
                multiple
                disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
              />
            </div>
          )}

          {type === AppointmentType.VIDEO && (
            <div className="space-y-2">
              <Label htmlFor="videoConferenceLink">Video-Konferenz Link</Label>
              <div className="relative">
                <Link className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="videoConferenceLink"
                  {...register('videoConferenceLink')}
                  placeholder="z.B. https://meet.google.com/..."
                  className="pl-10"
                  disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reminder">Erinnerung</Label>
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-gray-400" />
              <Select
                value={reminderMinutes?.toString() || '15'}
                onValueChange={(value) => setValue('reminders.0.minutesBefore', parseInt(value))}
                disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reminderOptions.map(option => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Weitere Details zum Termin..."
              rows={3}
              disabled={appointment?.status !== AppointmentStatus.SCHEDULED && appointment !== null}
            />
          </div>

          {appointment && defaultDealId && (
            <input type="hidden" {...register('dealId')} value={defaultDealId} />
          )}
          {defaultContactId && (
            <input type="hidden" {...register('contactId')} value={defaultContactId} />
          )}

          {appointment?.status === AppointmentStatus.SCHEDULED && onMarkComplete && (
            <div className="border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCompleteNotes(!showCompleteNotes)}
                className="w-full"
              >
                Als erledigt markieren
              </Button>
              {showCompleteNotes && (
                <div className="mt-2 space-y-2">
                  <Textarea
                    placeholder="Notizen zum Termin (optional)..."
                    value={completeNotes}
                    onChange={(e) => setCompleteNotes(e.target.value)}
                    rows={2}
                  />
                  <Button
                    type="button"
                    onClick={handleMarkComplete}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    Erledigt
                  </Button>
                </div>
              )}
            </div>
          )}
        </form>

        <DialogFooter className="flex justify-between">
          <div>
            {appointment && appointment.status === AppointmentStatus.SCHEDULED && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                Absagen
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            {(!appointment || appointment.status === AppointmentStatus.SCHEDULED) && (
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                {appointment ? 'Speichern' : 'Erstellen'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
